import React, { useCallback, useMemo, useState } from "react";
import { toPng } from "html-to-image";
import PieChartSubCategory from "components/ReportsItems/PieChartSubCategory";
import List from "components/common/ListWidget";
import moment from "moment";
import { useBusinessContext } from "BusinessApp";
import {
  useMostSoldItem,
  useOrderItemSummary,
} from "graphql/useOrderItemSummary";
import PieChartCategory from "components/ReportsItems/PieChartCategory";
import useItems from "graphql/useItems";
import { utils, writeFile } from "xlsx";
import { useTranslation } from "react-i18next";
import TopMenu from "components/Reports/TopMenu";
import { getDateHeader, mapToEntity } from "helpers/dataHelper";
import DocumentPDF from "./DocumentPDF";
import useSubcategories, {
  useCategories,
} from "../../graphql/useSubcategories";
import s from "./styles";
import { printDocumentPDF } from "../../helpers/utils";

const SCAPE_VALUE = "\\";

function fixValueForQuery(value) {
  return value
    .replace(`${SCAPE_VALUE}`, `${SCAPE_VALUE}${SCAPE_VALUE}`)
    .replace("*", `${SCAPE_VALUE}*`);
}

function ReportsItems() {
  const { t } = useTranslation();
  const { storeId, currencyFormat } = useBusinessContext();
  const [search, setSearch] = useState("");
  const [sorting, setSorting] = useState({ columnId: "quantity", order: 0 });
  const [date, setDate] = useState({
    value: moment(),
    periodType: "date",
    range: { start: moment().startOf("day"), end: moment().endOf("day") },
  });

  const firstGraph = React.useRef(null);
  const secondGraph = React.useRef(null);

  const [pagination, setPagination] = useState(10);

  // TODO: PCP-1860
  const { subcategories, loading: loadingSubCategories } = useSubcategories();
  const { categories, loading: loadingCategories } = useCategories();

  const { orderItemSummary, loading: loading1 } = useOrderItemSummary({
    storeId,
    start: date.range.start.toDate(),
    end: date.range.end.toDate(),
    limit: 100,
    type: null,
  });

  const { orderItemSummary: creOrderItemSummary, loading: loading2 } =
    useOrderItemSummary({
      storeId,
      start: date.range.start.toDate(),
      end: date.range.end.toDate(),
      type: "cre",
      limit: 100,
      sort: "quantity",
    });

  const { items, loading: loading3 } = useItems();

  const { itemsFiltered } = useMemo(() => {
    const itemsMap = new Map();
    creOrderItemSummary.forEach((v2) => {
      itemsMap.set(v2._id.itemId, v2.total);
    });

    const itemsFiltered = orderItemSummary
      .map((v) => {
        const item = items.find((item) => item._id === v._id.itemId);
        const sub = subcategories.find(
          (sub) => sub._id === item?.subcategoryId
        );
        const subCategorieFind = subcategories.find(
          (c) => c._id === v._id.subcategoryId
        );
        const categorieFind = categories.find(
          (c) => c._id === subCategorieFind?.categoryId
        );
        const cat = categories.find((cat) => cat._id === sub?.categoryId);

        const total = itemsMap.get(v._id.itemId);

        return [
          item?.sku || t("general.deleteItem"),
          v._id.itemName,
          cat?.name || categorieFind?.name || "-",
          v._id.subcategoryName || "-",
          `${currencyFormat(total || 0)}`,
          parseInt(v.quantity),
        ];
      })
      .filter((i) => {
        const regex = new RegExp(fixValueForQuery(search), "i");
        return i[0]?.match(regex) || i[1]?.match(regex);
      });

    return {
      itemsFiltered,
    };
  }, [
    orderItemSummary,
    creOrderItemSummary,
    items,
    subcategories,
    categories,
    search,
  ]);

  const mostSoldItems = useMostSoldItem(orderItemSummary);

  const itemsMap = useMemo(() => mapToEntity(items), [items]);

  /**
   * curated data, with all needed extra fields
   */
  const curatedMstSoldItems = useMemo(() => {
    let sortedItems = [...mostSoldItems];
    const compareItems = (a, b) => {
      const aValue = a[sorting.columnId];
      const bValue = b[sorting.columnId];
      if (aValue > bValue) {
        return -sorting.order;
      }
      if (aValue < bValue) {
        return sorting.order;
      }
      return 0;
    };
    sortedItems.sort(compareItems);
    const discount = (item, itemsMap) => {
      const subtotal = itemsMap[item.key]?.price * item.quantity - item.total;
      return subtotal < 0 ? 0 : subtotal;
    };
    return (
      sortedItems
        .filter((itemFilter) => itemFilter.key !== null)
        .filter((i) => {
          const regex = new RegExp(fixValueForQuery(search), "i");
          return i.name?.match(regex) || i.group?.match(regex);
        })
        // we add discount
        .map((item) => ({
          ...item,
          sku: itemsMap[item.key]?.sku,
          price: itemsMap[item.key]?.price || 0,
          discount: discount(item, itemsMap) || 0,
        }))
        .slice(0, pagination)
    );
  }, [mostSoldItems, pagination, itemsMap, sorting]);

  const orderMostSoldItemsSummaryMapped = useMemo(() => {
    return curatedMstSoldItems.map((item) => {
      return [
        item.sku || <p>{t("general.deleteItem")}</p>,
        item.name || "-",
        item.group || "-",
        currencyFormat(item.price),
        currencyFormat(item.discount),
        currencyFormat(item.total),
        parseFloat(item.quantity),
      ];
    });
    // ???
  }, [mostSoldItems]);

  const { higherRevenueItem, sellingUnits } = useMemo(() => {
    const filteredItems = mostSoldItems.filter(
      (itemFilter) => itemFilter.key !== null
    );

    const higherRevenueItem = [...filteredItems].sort(
      (a, b) => b.total - a.total
    );
    const sellingUnits = [...filteredItems].sort(
      (a, b) => b.quantity - a.quantity
    );

    return {
      higherRevenueItem,
      sellingUnits,
    };
  }, [mostSoldItems]);

  const loading =
    loadingSubCategories ||
    loadingCategories ||
    loading1 ||
    loading2 ||
    loading3;

  const { bestSellingItems, bestSellingUnits } = useMemo(() => {
    const totalOfRest = higherRevenueItem
      .slice(3)
      .reduce((sum, item) => sum + parseFloat(item.total), 0);

    const bestSellingItems = [
      ...higherRevenueItem.slice(0, 3),
      {
        name: "otros",
        total: totalOfRest.toFixed(2),
      },
    ].map((item) => [item.name, parseFloat(item.total)]);

    const totalOfRestUnits = sellingUnits
      .slice(3)
      .reduce((sum, item) => sum + parseFloat(item.quantity), 0);

    const bestSellingUnits = [
      ...sellingUnits.slice(0, 3),
      {
        name: "otros",
        quantity: totalOfRestUnits.toFixed(2),
      },
    ].map((item) => [item.name, parseFloat(item.quantity)]);

    return {
      bestSellingItems,
      bestSellingUnits,
    };
  }, [higherRevenueItem, sellingUnits]);

  const exportFileExcel = useCallback(() => {
    const worksheetData = [
      [
        `${t("containers.ReportsItems.ReportsItemsFor")} ${getDateHeader(
          date.range.start,
          date.range.end
        )}`,
      ],
      [],
      [t("containers.ReportsItems.bestSellingItems")],
      ...bestSellingItems,
      [],
      [t("containers.ReportsItems.bestSellingUnits")],
      ...bestSellingUnits,
      [],
      [t("containers.ReportsItems.bestSellingItems")],
      [
        t("containers.ReportsItems.sku"),
        t("containers.ReportsItems.item"),
        t("containers.ReportsItems.subcategorie"),
        t("containers.ReportsItems.price"),
        t("containers.ReportsItems.discount"),
        t("containers.ReportsItems.income"),
        t("containers.ReportsItems.soldUnits"),
      ],
    ];

    curatedMstSoldItems.forEach((item) => {
      /**
       const values = Object.values(item).map((value) => {
       if (typeof value === "string" && value.includes("€")) {
       const numberValue = parseFloat(
       value
       .replace(/[^\d,-]/g, "")
       .replace(",", ".")
       .replace(" ", "")
       );
       return isNaN(numberValue) ? value : numberValue;
       }
       return value;
       */
      worksheetData.push([
        item.sku,
        item.name,
        item.group,
        item.price,
        item.discount,
        item.total,
        item.quantity,
      ]);
    });

    const workbook = utils.book_new();
    const sheetName = t("containers.ReportsItems.ReportsItems");
    const worksheet = utils.aoa_to_sheet(worksheetData);
    utils.book_append_sheet(workbook, worksheet, sheetName);

    writeFile(workbook, `${t("containers.ReportsItems.ReportsItems")}.xlsx`);
  }, [
    date.range.start,
    date.range.end,
    bestSellingItems,
    bestSellingUnits,
    itemsFiltered,
    curatedMstSoldItems,
  ]);
  const getImageGraph = useCallback(async () => {
    if (firstGraph.current === null || secondGraph.current === null) {
      return;
    }
    const img1 = await toPng(firstGraph.current, { cacheBust: true });

    const img2 = await toPng(secondGraph.current, { cacheBust: true });

    await printDocumentPDF(
      <DocumentPDF
        data={{
          dateHeader: getDateHeader(date.range.start, date.range.end)[0],
          img1,
          img2,
          orderMostSoldItemsSummaryMapped,
        }}
      />,
      `${t("containers.ReportsItems.ReportsItems")}.pdf`
    );
  }, [date.range.end, date.range.start, orderMostSoldItemsSummaryMapped]);

  return (
    <s.PageContainer>
      <TopMenu
        value={date.value}
        onChange={(value, periodType, range) => {
          setDate({ value, periodType, range });
        }}
        setSearch={setSearch}
        exportFileExcel={exportFileExcel}
        getImageGraph={getImageGraph}
        disabled={orderItemSummary.length === 0}
        pagination={pagination}
        setPagination={setPagination}
      />

      <s.Container>
        <s.ChartComponentsWrapper>
          <PieChartCategory
            firstGraph={firstGraph}
            orderItemCategorySummary={higherRevenueItem}
            loadingCategories={loading}
          />
          <PieChartSubCategory
            secondGraph={secondGraph}
            SubcategorySummary={sellingUnits}
            loadingSubCategories={loading}
          />
        </s.ChartComponentsWrapper>
      </s.Container>
      <s.Container>
        <List
          width="800px"
          items={orderMostSoldItemsSummaryMapped}
          title={t("containers.ReportsItems.bestSellingItems")}
          columns={[
            {
              title: t("containers.ReportsItems.sku"),
            },
            {
              title: t("containers.ReportsItems.item"),
              sortable: false,
              columnId: "itemName",
            },
            {
              title: t("containers.ReportsItems.subcategorie"),
              sortable: false,
              columnId: "name",
            },
            {
              title: t("containers.ReportsItems.price"),
              sortable: false,
              columnId: "subcategoryName",
            },
            {
              title: t("containers.ReportsItems.discount"),
              sortable: false,
              columnId: "subcategoryName",
            },
            {
              title: t("containers.ReportsItems.income"),
              sortable: true,
              columnId: "total",
            },
            {
              title: t("containers.ReportsItems.soldUnits"),
              sortable: true,
              columnId: "quantity",
            },
          ]}
          tableStyle={{
            gridTemplateColumns: "21fr 21fr 15fr 15fr 15fr 15fr 19fr",
          }}
          sorting={sorting}
          changeSorting={(columnId, order) => setSorting({ columnId, order })}
          loading={loading}
        />
      </s.Container>
    </s.PageContainer>
  );
}

export default ReportsItems;
